// Dot pen decorator — wraps a `StampRenderer` so `line()` only stamps
// spacing-separated dots instead of a continuous sweep (`pen='dot'`,
// phase11-light-pens-2026-08-21.md §3).
//
// ## No new kernel (§3.2)
//
// This is a SEAM DECORATOR, not a kernel. It implements `dot()`/`line()` by
// calling straight through to (`dot`) or thinning down to (`line`) an inner
// `StampRenderer` — the actual per-pixel stamping is whatever the inner
// renderer already does (plain JS `createJsStampRenderer()` for `pen='dot'`
// today; the design deliberately keeps this swappable — the server's
// Wasm-accelerated snapshot path wraps a *collector* renderer instead, see
// "Formerly a known gap — CLOSED" below).
//
// ## Phase accumulator (why this is deterministic — §3.3)
//
// The decorator tracks "how many more sweep-steps until the next dot" as a
// closure-local `phase`, mutated only inside `line()`. This is safe (unlike
// the stateful PRNG `airbrush-design §1.1` rejected) for 4 reasons, mirrored
// from the design doc verbatim so the reasoning stays next to the code:
//
//   1. **Per-stroke, not shared.** `resolveStampRenderer('dot', ...)` is
//      called once per `startStroke` and the resulting closure is stored on
//      that stroke's `StrokeState` (`strokeRenderer.ts`). No two strokes —
//      not even from the same user — ever share a `phase` variable, so
//      interleaved strokes from different senders cannot advance each
//      other's phase.
//   2. **Progresses only by geometry.** `phase` advances by sweep-step count
//      (`i = 1..steps`, a pure function of the segment endpoints), never by
//      whether a stamp actually painted anything. A stamp skipped by mask
//      gating or an Undo `clip` still consumes exactly one step of `phase` —
//      the decorator never even sees those outcomes (they're decided inside
//      the inner renderer's per-pixel kernel, after this decorator has
//      already called `dot()`).
//   3. **Replay re-derives it.** Undo's bbox-local repair re-runs the same
//      `startStroke`/`addPoints`/`endStroke` sequence (`UndoBaselineStore.ts`)
//      through a freshly resolved renderer, so the SAME point sequence
//      reproduces the SAME phase sequence.
//   4. **Wire points are identical everywhere.** Local rendering, peer
//      broadcast replay, and server snapshot replay all drive the exact same
//      point sequence through `processPoint` (`StrokeState.pointIndex`'s own
//      doc makes the same argument for airbrush) — so client / peer / server
//      phase sequences match.
//
// The one place phase does NOT match the eventual committed sequence is the
// lead-overlay preview (`rasterizeLeadSegment`) — it resolves its own
// throwaway renderer per rAF tick, so its phase starts fresh each time. This
// mirrors the airbrush `leadPointIndex` non-parity the design doc explicitly
// accepts (see that function's header comment) — the lead is never committed
// or sent over the wire, so there is no parity contract to keep; the visible
// effect is limited to the spaced dots "regrouping" by up to one dot-spacing
// when a lead segment commits.
//
// ## Formerly a known gap — CLOSED (phase11-light-pens-2026-08-21.md §3, plan A)
//
// `resolveStampRenderer` still only ever builds THIS decorator (the one in
// this file) around the JS `createJsStampRenderer()` — the Wasm-accelerated
// snapshot path (`WasmRasterizerBridge`, `WASM_RASTERIZER_ENABLED`) does NOT
// use this file's decorator at all, because it doesn't drive per-pen
// `StampRenderer.dot()`/`.line()` calls the way `StrokeRenderer.processPoint`
// does — it batches raw wire points straight into the Rust `StrokeKernel`
// ABI, which sweeps between consecutive `add_points*` points INSIDE Rust and
// has no "stamp an isolated dot, don't connect it to the last point"
// primitive.
//
// The fix is NOT a second Rust-side reimplementation of the phase logic
// above (that would risk the two drifting — the exact failure mode §1.2's
// airbrush doc warns about for a similar reason). Instead
// `WasmRasterizerBridge.ts` runs THIS SAME `wrapWithDotSpacing` decorator
// (imported from this file) around a throwaway "collector" `StampRenderer`
// that records the resolved `(x, y, widthScale, alphaScale)` tuples instead
// of painting, then forwards just those already-thinned tuples to a new
// isolated-stamp Rust ABI (`LayerStrokeHandle::add_isolated_points_pressured`,
// `lib.rs`) that stamps each one without sweeping and without touching
// Rust's internal `last_point`/`is_first` chain. The phase computation stays
// in exactly ONE place (here); Rust only ever receives pre-decided points.
// See `WasmRasterizerBridge.ts`'s `pen === 'dot'` branch for the call site,
// and `SnapshotEngine.parity.test.ts`'s dot pen block for the byte-identical
// parity coverage (both `WASM_RASTERIZER_ENABLED` on and off).
/**
 * Default spacing (sweep-steps) when a `pen='dot'` stroke's `penParams` omit
 * it or carry a mismatched `kind` (malformed/legacy replay safety net —
 * `spacing` is wire-required for a well-formed dot stroke, see
 * `penParamsSchema`'s `dot` arm doc, but the render side still needs SOME
 * safe fallback, same spirit as every other pen's `resolveStampRenderer`
 * default).
 */
export const DOT_DEFAULT_SPACING = 8;
/**
 * Wrap `inner` so `line()` only forwards spacing-separated points to
 * `inner.dot()`; `dot()` (a lone tap, no sweep to thin) passes straight
 * through unchanged.
 *
 * `spacing` is in sweep-step units (`steps = ceil(max(|dx|,|dy|))`,
 * Chebyshev distance — §3.4), clamped to a minimum of 1 (a spacing < 1 would
 * be indistinguishable from — and more expensive than — spacing 1).
 */
export const wrapWithDotSpacing = (inner, spacing) => {
    const step = spacing >= 1 ? spacing : 1;
    // Sweep-steps remaining until the next dot lands, minus 1 (see below).
    // Starts at `step - 1` so the FIRST dot of the very first `line()` call
    // lands exactly `step` sweep-steps after the segment's start endpoint —
    // i.e. `step` steps after the stroke's initial `dot()` stamp (that initial
    // point is stamped separately, outside this decorator, by the very first
    // `processPoint` call — see `strokeRenderer.ts`). Without the `- 1`, the
    // first dot would land 1 step too early (at `i = step + 1` instead of `i =
    // step`, since the `phase <= 0` check happens BEFORE this iteration's
    // decrement — see the loop body). Reset the same way on every `dot()`
    // call, so a `line()` sweep immediately following a lone tap also starts
    // counting a fresh `step`-sized gap from that tap (defensive — `dot()` is
    // normally called at most once per stroke, as the very first point, so
    // this reset is a no-op in practice today).
    let phase = step - 1;
    return {
        dot(args) {
            phase = step - 1;
            inner.dot(args);
        },
        line(args) {
            const dxAbs = Math.abs(args.x2 - args.x1);
            const dyAbs = Math.abs(args.y2 - args.y1);
            // Mirrors `drawLine`'s own sweep-step count exactly (rasterizer.ts) so
            // the decorator's notion of "how many steps this segment covers" never
            // drifts from the inner renderer's own half-open sweep contract.
            const steps = Math.max(1, Math.ceil(Math.max(dxAbs, dyAbs)));
            const wScaleStart = args.widthScaleStart ?? 1;
            const wScaleEnd = args.widthScaleEnd ?? 1;
            const aScaleStart = args.alphaScaleStart ?? 1;
            const aScaleEnd = args.alphaScaleEnd ?? 1;
            // i = 1..steps — same half-open convention `drawLine` documents (the
            // segment's start endpoint is never re-stamped; it was already stamped
            // as the previous segment's END or by the stroke's initial `dot()`).
            for (let i = 1; i <= steps; i++) {
                if (phase <= 0) {
                    const t = i / steps;
                    const x = args.x1 + (args.x2 - args.x1) * t;
                    const y = args.y1 + (args.y2 - args.y1) * t;
                    // Interpolate the pressure scales the same way `drawLine` would at
                    // this sweep position, so a spaced-out dot still tapers with
                    // pressure the way the (thinned-away) continuous sweep would have.
                    const widthScale = wScaleStart + (wScaleEnd - wScaleStart) * t;
                    const alphaScale = aScaleStart + (aScaleEnd - aScaleStart) * t;
                    inner.dot({
                        buffer: args.buffer,
                        mainRgba: args.mainRgba,
                        maskSnapshot: args.maskSnapshot,
                        width: args.width,
                        height: args.height,
                        x,
                        y,
                        penAlpha: args.penAlpha,
                        penWidth: args.penWidth,
                        mask: args.mask,
                        accumulate: args.accumulate,
                        aa: args.aa,
                        // `drawDot` reads `widthScaleEnd ?? widthScaleStart ?? 1`; setting
                        // both to the SAME interpolated value keeps this correct
                        // regardless of which field the inner renderer happens to read.
                        widthScaleStart: widthScale,
                        widthScaleEnd: widthScale,
                        alphaScaleStart: alphaScale,
                        alphaScaleEnd: alphaScale,
                        pointIndex: args.pointIndex,
                        clip: args.clip,
                    });
                    phase = step;
                }
                phase -= 1;
            }
        },
    };
};
//# sourceMappingURL=dotDecorator.js.map